import offlineOverlayStyles from './offline-overlay.styles.css?inline';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { NetworkStateNotifier } from '../utility/notifications/network-state-notifier';
import type { LiveUpdateState } from '../utility/notifications/live-update-transport';
import { OFFLINE_WARNING } from '../utility/notifications/connection-health-words';

/** How the live-update connection's state reads on the overlay. */
const LIVE_UPDATE_WORDS: Record<LiveUpdateState, string> = {
  connected: 'connected',
  reconnecting: 'reconnecting',
  disconnected: 'disconnected',
};

/**
 * The blocking overlay shown while the page is OFFLINE, and only then (D-137, rule 3,
 * @CONN-HEALTH-002 and 003).
 *
 * Offline is the connection-health monitor's decision: the server did not answer its liveness
 * check, or the browser reports no network. The live-update connection dropping is NOT offline and
 * never shows this (rule 2). The overlay shows both the server's state and the live updates' state,
 * counts down to the monitor's next reachability check (not the live-update transport's own retry,
 * which is no longer what brings the page back), and clears when the monitor leaves offline,
 * whatever the live updates are doing.
 */
@customElement('offline-overlay')
export class OfflineOverlay extends LitElement {
  static styles = unsafeCSS(offlineOverlayStyles);

  @property({ type: Boolean, reflect: true }) active = false;

  @state() private secondsUntilCheck: number | undefined;
  @state() private liveUpdates: LiveUpdateState = 'disconnected';
  @state() private browserOffline = false;

  private _unsubscribe?: () => void;
  private _countdownTimer?: ReturnType<typeof setInterval>;

  connectedCallback(): void {
    super.connectedCallback();
    const notifier = NetworkStateNotifier.getInstance();
    this._unsubscribe = notifier.onChange(() => this.sync());
    this.sync();
  }

  disconnectedCallback(): void {
    this._unsubscribe?.();
    this._stopCountdown();
    super.disconnectedCallback();
  }

  /** Follow the monitor's latest decision. */
  private sync(): void {
    const snapshot = NetworkStateNotifier.getInstance().snapshot;
    this.liveUpdates = snapshot.liveUpdates;
    this.browserOffline = !navigator.onLine;
    const shouldBeActive = snapshot.health === 'offline';
    if (shouldBeActive !== this.active) {
      this.active = shouldBeActive;
    }
    if (this.active) {
      this._startCountdown();
    } else {
      this._stopCountdown();
    }
  }

  private _updateCountdown() {
    const next =
      NetworkStateNotifier.getInstance().snapshot.nextReachabilityCheckAt;
    if (!this.active || next === undefined) {
      this.secondsUntilCheck = undefined;
      return;
    }
    this.secondsUntilCheck = Math.max(0, Math.ceil((next - Date.now()) / 1000));
  }

  private _startCountdown() {
    this._updateCountdown();
    if (!this._countdownTimer) {
      this._countdownTimer = setInterval(() => this._updateCountdown(), 1000);
    }
  }

  private _stopCountdown() {
    if (this._countdownTimer) clearInterval(this._countdownTimer);
    this._countdownTimer = undefined;
    this.secondsUntilCheck = undefined;
  }

  private _renderCountdownSentence() {
    const secs = this.secondsUntilCheck;
    if (secs === undefined || secs <= 0) {
      return html`Checking the server now.`;
    }
    const plural = secs === 1 ? '' : 's';
    return html`Next check in <strong>${secs}</strong> second${plural}.`;
  }

  render() {
    const serverWords = this.browserOffline
      ? 'no network connection'
      : 'not reachable';
    return html`
      <div class="backdrop" role="alert" aria-live="assertive">
        <div class="panel">
          <div class="message">
            <span class="status-dot" aria-hidden="true"></span>System Offline
          </div>
          <h2>Please wait for automatic reconnection</h2>
          <dl class="states">
            <div class="state-row">
              <dt>Server</dt>
              <dd data-state="server">${serverWords}</dd>
            </div>
            <div class="state-row">
              <dt>Live updates</dt>
              <dd data-state="live-updates">
                ${LIVE_UPDATE_WORDS[this.liveUpdates]}
              </dd>
            </div>
          </dl>
          <p class="warning">${OFFLINE_WARNING}</p>
          <p>${this._renderCountdownSentence()}</p>
          <div class="hint">Input temporarily disabled</div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'offline-overlay': OfflineOverlay;
  }
}
